import React, { FC, useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AssistantCommunicationStyle,
  AssistantEnergyLevel,
} from '@dreamai/types';
import api from '../../../api';
import { SetPersonalityStepProps } from '../OnboardingFlow';
import './set-personality-step.scss';

const SHROUDED_DREAMIE_URL =
  'https://d12b9vnf4u4q7j.cloudfront.net/dreamies/shrouded-dreamie.png';

const COMMUNICATION_STYLES: {
  value: AssistantCommunicationStyle;
  label: string;
}[] = [
  { value: 'professional', label: '👨‍✈️ Professional' },
  { value: 'warm', label: '☀️ Warm' },
  { value: 'friendly', label: '😘 Friendly' },
  { value: 'direct', label: '☝️ Direct' },
  { value: 'diplomatic', label: '🙇 Diplomatic' },
  { value: 'humorous', label: '🤣 Humorous' },
  { value: 'approachable', label: '🤗 Approachable' },
];

const ENERGY_LEVELS: {
  value: AssistantEnergyLevel;
  label: string;
}[] = [
  { value: 'energetic', label: '⚡️ Energetic' },
  { value: 'calm', label: '⛅️ Calm' },
  { value: 'decisive', label: '🎯 Decisive' },
  { value: 'supportive', label: '🚀 Supportive' },
];

const PlusCircle: FC = () => (
  <svg viewBox='0 0 20 20' fill='none' className='chip-icon'>
    <g clipPath='url(#pc)'>
      <path
        d='M10 6.667v6.666M6.667 10h6.666M18.333 10a8.333 8.333 0 1 1-16.666 0 8.333 8.333 0 0 1 16.666 0Z'
        stroke='#838B9B'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='pc'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

const CheckCircle: FC = () => (
  <svg viewBox='0 0 20 20' fill='none' className='chip-icon'>
    <g clipPath='url(#cc)'>
      <path
        d='M6.25 10l2.5 2.5 5-5M18.333 10a8.333 8.333 0 1 1-16.666 0 8.333 8.333 0 0 1 16.666 0Z'
        stroke='#FFD601'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='cc'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

const SetPersonalityStep: FC<SetPersonalityStepProps> = ({ onComplete }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [showIntro, setShowIntro] = useState(true);
  const [selectedStyles, setSelectedStyles] = useState<
    AssistantCommunicationStyle[]
  >([]);
  const [selectedEnergy, setSelectedEnergy] =
    useState<AssistantEnergyLevel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const preloadedEnergyRef = useRef<string | null>(null);

  // Pre-load candidate images when an energy level is selected
  useEffect(() => {
    if (!selectedEnergy || selectedEnergy === preloadedEnergyRef.current)
      return;
    preloadedEnergyRef.current = selectedEnergy;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.post<{
          previews: { imageUrl: string }[];
        }>(
          '/assistant-onboarding/candidates/preview',
          {
            energyLevels: [selectedEnergy],
            agentType: 'AI Secretary',
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Preload each image into browser cache (no crossOrigin so
        // cache keys match the regular <img> tags in ChooseProfileStep)
        (res.data.previews || []).forEach((p) => {
          if (p.imageUrl) {
            const img = new Image();
            img.src = p.imageUrl;
          }
        });
      } catch {
        // Silent — preloading is best-effort
      }
    })();
  }, [selectedEnergy, getAccessTokenSilently]);

  const toggleStyle = (style: AssistantCommunicationStyle) => {
    setSelectedStyles((prev) =>
      prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style],
    );
    if (error) setError(null);
  };

  const toggleEnergy = (energy: AssistantEnergyLevel) => {
    setSelectedEnergy((prev) => (prev === energy ? null : energy));
    if (error) setError(null);
  };

  const handleSubmit = () => {
    if (selectedStyles.length === 0 || !selectedEnergy) {
      setError(
        'Please select at least one communication style and an energy level.',
      );
      return;
    }

    // Store personality preferences for the next step (ChooseProfileStep)
    // which will combine them with gender selection to call the API
    sessionStorage.setItem(
      'onboarding_personality',
      JSON.stringify({
        communicationStyles: selectedStyles,
        energyLevels: [selectedEnergy],
      }),
    );

    onComplete();
  };

  const isValid = selectedStyles.length > 0 && selectedEnergy !== null;

  if (showIntro) {
    return (
      <div className='onboarding-step set-personality-step'>
        <div className='step-header'>
          <h1>Create your secretary</h1>
        </div>

        <div className='step-inner intro-inner'>
          <div className='intro-text'>
            <p className='intro-main'>
              It's time to start creating your secretary.
            </p>
            <p className='intro-bold'>
              You'll be able to choose how they look, behave, and talk to you.
            </p>
          </div>

          <div className='intro-image-wrapper'>
            <img
              src={SHROUDED_DREAMIE_URL}
              alt='Your future secretary'
              className='intro-image'
            />
          </div>

          <button
            className='step-button primary-gradient'
            onClick={() => setShowIntro(false)}
          >
            <span>Get started</span>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <circle cx='12' cy='12' r='9' />
              <path d='M12 8l4 4-4 4M8 12h8' />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='onboarding-step set-personality-step'>
      <div className='step-header'>
        <h1>What's my personality?</h1>
      </div>

      <div className='step-inner'>
        <div className='personality-intro'>
          <p className='personality-description'>
            This affects how I speak to you, and react to your requests. I'll
            always have a strong attention to detail, be capable and clever.
          </p>
        </div>

        <div className='personality-content'>
          <div className='personality-image-wrapper'>
            <img
              src={SHROUDED_DREAMIE_URL}
              alt='Your future secretary'
              className='personality-image'
            />
          </div>

          <div className='personality-card'>
            <div className='trait-section'>
              <div className='trait-header'>
                <span>How does your secretary talk?</span>
              </div>
              <div className='chip-grid'>
                {COMMUNICATION_STYLES.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`trait-chip ${selectedStyles.includes(value) ? 'selected' : ''}`}
                    onClick={() => toggleStyle(value)}
                  >
                    <span>{label}</span>
                    {selectedStyles.includes(value) ? (
                      <CheckCircle />
                    ) : (
                      <PlusCircle />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className='trait-section'>
              <div className='trait-header'>
                <span>What energy do they have?</span>
              </div>
              <div className='chip-grid'>
                {ENERGY_LEVELS.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`trait-chip ${selectedEnergy === value ? 'selected' : ''}`}
                    onClick={() => toggleEnergy(value)}
                  >
                    <span>{label}</span>
                    {selectedEnergy === value ? (
                      <CheckCircle />
                    ) : (
                      <PlusCircle />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {error && <p className='personality-error'>{error}</p>}
      </div>

      <div className='step-footer'>
        <button
          className='step-button primary-gradient'
          onClick={handleSubmit}
          disabled={!isValid}
        >
          <span>Save and continue</span>
          <svg
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
          >
            <circle cx='12' cy='12' r='9' />
            <path d='M12 8l4 4-4 4M8 12h8' />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SetPersonalityStep;
